import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MATERIAL_IMPORTS } from '../../../../material/material-imports';
import { CapitalizePipe } from '../../../../shared/pipes/capitalize.pipe';
import { CkEditorComponent } from '../../../../shared/ck-editor/ck-editor.component';
import { Location } from '@angular/common';
import {FormGroup, Validators, FormControl, FormArray} from '@angular/forms';
import { FormService } from '../../../../shared/form.service';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { SolutionModel, SolutionRequest, ProblemModel } from '@generativ/much-smarter-models';
import { Observable } from 'rxjs';

import { ParamsService } from '../../../../shared/params.service';
import { ProblemService } from '../../../../shared/problem.service';
import { SolutionService } from '../solution.service';
import { AlertService } from '../../../../core/alert/alert.service';


@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ...MATERIAL_IMPORTS, CapitalizePipe, CkEditorComponent],
  selector: 'app-solution',
  templateUrl: './solution.component.html',
  styleUrls: ['./solution.component.scss']
})
export class SolutionComponent implements OnInit {

  solutionId: number;
  problemId: number;
  solutionForm: FormGroup;
  pageType: string;
  problem: ProblemModel;
  errorMessage: string;
  isSubmitting = false;

  wrongAnswersString: string[] = ['Wrong Answer. Click here to edit '];

  constructor(private route: ActivatedRoute,
              private router: Router,
              private solutionService: SolutionService,
              private problemService: ProblemService,
              private alertService: AlertService,
              private paramsService: ParamsService,
              private location: Location,
              public formService: FormService) {
    this.solutionForm = this.formService.createForm(this.formOptions);
  }

  ngOnInit() {
    this.problemId = +this.paramsService.getParamById('problemId', this.route.snapshot);
    this.route.data.subscribe((data) => {
      this.pageType = data.type;
      if (this.pageType === 'edit') {
        this.route.params.subscribe((params: Params) => {
          this.solutionId = +params['solutionId'];
          this.problemId = +params['problemId'];
          this.solutionService.getSolution(this.solutionId).subscribe(
            solutionModel => {
              // Wrong Answer are storaged as string we should parse it to json
              this.wrongAnswersString = JSON.parse(solutionModel.wrongAnswers);
              this.solutionForm.patchValue(solutionModel);
            }
          );
        });
      }
      this.problemService.getProblem(this.problemId).subscribe(
        problemModel => {
          this.problem = problemModel;
        }
      );
    });
  }

  submitForm() {
    this.solutionForm.get('wrongAnswers').setValue(JSON.stringify(this.wrongAnswersString));
    if (this.formService.validateFormAndUpdateErrors(this.solutionForm)) {
      let solutionObservable: Observable<SolutionModel>;
      let message: string;
      this.errorMessage = null;

      if (this.isSubmitting) {
        return;
      }

      if (!this.solutionForm.valid) {
        this.errorMessage = 'Errors on form. See fields below.';
        return;
      }

      this.isSubmitting = true;

      if (this.pageType === 'create') {
        const solution = new SolutionRequest.Create();
        solution.parse(this.solutionForm.value);
        solution.problemId = this.problemId;
        message = 'Solution created';
        solutionObservable = this.solutionService.createSolution(solution);
      } else {
        const solution = new SolutionRequest.Update();
        solution.parse(this.solutionForm.value);
        message = 'Solution edited successfully';
        solutionObservable = this.solutionService.updateSolution(this.solutionId, solution);
      }
      solutionObservable.subscribe(
        solutionModel => {
          this.alertService.success(message);
          if (this.pageType === 'create') {
            this.router.navigate([`../../manage`], {relativeTo: this.route});
          } else {
            this.location.back();
          }
        },
        err => {
          this.isSubmitting = false;
          this.alertService.error(err);
        }
      );
    }
  }

  /**
   * Generate one array with index. Eg: If wrongAnswersString length is 3 it will generate [0,1,2]
   * I need to use it bc if we use the normal <li *ngFor="let item of wrongAnswersString; let i = index">
   * it doesn't work fine.
   * @returns {Array<number>}
   */
  get arrayOfWrongAnswerIndex() {
    return Array.from(new Array(this.wrongAnswersString.length), (val, index) => index + 1);
  }


  /**
   * Custom validator used for checking that at least we are including 4 or 3 wrong answer depending on the
   * subject of the current problem.
   * @returns {{needsMoreWrongAnswers: boolean}}
   */
  hasMinimumWrongAnswers() {
    const hasMinimumWrongAnswers = this.wrongAnswersString && this.wrongAnswersString.length >= this.minimumNumber;
    return hasMinimumWrongAnswers ? null : {needsMoreWrongAnswers: true};
  }

  /**
   * Custom validator used for checking that we are including 3 wrong answers
   * @returns {{hasThreeWrongAsnwers: boolean}}
   */
  hasThreeWrongAnswers() {
    const hasThreeWrongAnswers = this.wrongAnswersString && this.wrongAnswersString.length == 3;
    return hasThreeWrongAnswers ? null : {needsThreeWrongAnswers: true};
  }

  /**
   * Returns if current problem subject is ACT Math.
   * @returns {ProblemModel|TopicModel|boolean}
   */
  get isMathProblem(): boolean {
    return this.problem && this.problem.topic && this.problem.topic.subject === 'ACT Math';
  }

  /**
   * Returns 4 if problem subject es ACT Math 3 in other case.
   * @returns {ProblemModel|TopicModel|boolean}
   */
  get minimumNumber(): number {
    return this.isMathProblem ? 4 : 3;
  }


  addWrongAnswer() {
    this.wrongAnswersString.push('Wrong Answer. Click here to edit ');
    this.solutionForm.get('wrongAnswers').setValue(JSON.stringify(this.wrongAnswersString));
    this.solutionForm.get('wrongAnswers').markAsDirty();
  }

  removeWrongAnswer(index: number) {
    this.wrongAnswersString.splice(index, 1);
    this.solutionForm.get('wrongAnswers').setValue(JSON.stringify(this.wrongAnswersString));
    this.solutionForm.get('wrongAnswers').markAsDirty();
  }

  private  get formOptions(): object {
    return {
      'title': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      'correctAnswer': [
        'Correct Answer. Click here to edit ',
        Validators.required,
      ],
      'wrongAnswers': [
        null,
        [
          Validators.required,
          // Pass this (objetc) to validator
          this.hasThreeWrongAnswers.bind(this)
        ]
      ],
      'problemId': []
    };
  }

}
